import React, { useEffect } from 'react'
import Typography from '@mui/material/Typography'
import { Field, useFormikContext } from 'formik'
import Paper from '@mui/material/Paper'
import FormControlLabel from '@mui/material/FormControlLabel'
import { InfoTooltip } from '@sales-center/components'
import Stack from '@mui/material/Stack'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import Checkbox from '@mui/material/Checkbox'
import { DefaultNoteModel } from '@user/components/Settings/Notes/typings'
import { NoteCode } from '@user/components/Notes/typings'
import NoteCodeSelect from '@user/components/Notes/AddNoteDrawer/AddNoteForm/NoteCodeSelect'
import { Theme } from '@mui/material/styles'

interface Props {
  noteCodes: NoteCode[]
  formDefaultValues: DefaultNoteModel
}

export function DefaultNoteCodeForm(props: Props) {
  const { noteCodes } = props
  const { externalProduct } = useSelector(selectCurrentUser)
  const {
    submitForm,
    setValues,
    setFieldValue,
    values: { defaultNoteCodeId, noteAutomationEnabled },
  } = useFormikContext<DefaultNoteModel>()

  useEffect(() => {
    const defaultNoteCodeExists = noteCodes.some(
      (x) => x.id === defaultNoteCodeId,
    )

    if (noteCodes.length === 0 || defaultNoteCodeExists) {
      return
    }
    setValues({
      defaultNoteCodeId: null,
      noteAutomationEnabled: false,
    })
    submitForm()
  }, [
    defaultNoteCodeId,
    noteCodes,
    noteAutomationEnabled,
    setValues,
    submitForm,
  ])
  return (
    <Paper
      variant="outlined"
      sx={{
        padding: (theme: Theme) => theme.spacing(1.6),
        width: '100%',
      }}
    >
      <Typography variant="h6">Default note code</Typography>
      <Typography variant="body2" mb={2}>
        This code will be auto-selected when employees add notes to PestPac
        locations from Sales Center
      </Typography>
      <NoteCodeSelect
        value={noteCodes.find((x) => x.id === defaultNoteCodeId)?.name || null}
        label={'Default Note Code'}
        name="defaultNoteCodeId"
        options={noteCodes}
        setFieldValue={(name, value) => {
          setFieldValue(name, noteCodes.find((x) => x.name === value)?.id)
          submitForm()
        }}
        selectWidth={'380px'}
        error={false}
      />
      <Typography variant="h6" mt={2.4}>
        Note Automation
      </Typography>
      <Stack direction="row" alignItems="center" mt={0} gap={0.5}>
        <FormControlLabel
          sx={{ width: 'fit-content', m: 0 }}
          labelPlacement="end"
          label={
            <Typography
              variant="body1"
              display="flex"
              alignItems="center"
              gap={0.5}
            >
              Retroactively add Sales Center notes to {externalProduct}
            </Typography>
          }
          control={
            <Field name="noteAutomationEnabled">
              {({ field }) => (
                <Checkbox
                  {...field}
                  disabled={!defaultNoteCodeId}
                  size="medium"
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e)
                    submitForm()
                  }}
                />
              )}
            </Field>
          }
        />

        <InfoTooltip
          fontSize={20}
          infoText={`When a location is matched to/created for a lead or opportunity, any existing Sales Center notes will automatically be added to the ${externalProduct} location(s). The default note code will be used.`}
        />
      </Stack>
    </Paper>
  )
}
